import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import {
  ReplaceOrderPayload,
  TimeInForce,
  tradingServices,
  TradingOrder,
} from '../../services/trading-service'
import {
  describeOrderType,
  isModifiable,
  isOpenOrder,
  TIME_IN_FORCE_LABEL,
  usd,
  usesLimit,
  usesStop,
} from './order-rules'

const errorMessage = (res: any, fallback: string): string =>
  res?.data?.error?.message || res?.data?.message || fallback

const tifChoices = (o: TradingOrder): TimeInForce[] => {
  if (o.extended_hours || (o.qty && !Number.isInteger(Number(o.qty)))) return ['day']
  if (o.type === 'trailing_stop') return ['day', 'gtc']
  if (usesStop(o.type)) return ['day', 'gtc', 'ioc', 'fok']
  return ['day', 'gtc', 'ioc', 'fok', 'opg', 'cls']
}

const asInput = (value: string | null | undefined) =>
  value === null || value === undefined ? '' : String(Number(value))

/**
 * Edits an open order. Only changed fields are sent; Alpaca cancels the
 * order and replaces it with a new one.
 */
const ModifyOrderForm = ({
  order,
  onDone,
  onCancel,
}: {
  order: TradingOrder
  onDone: (text: string) => void
  onCancel: () => void
}) => {
  const trailField = order.trail_percent ? 'trailPercent' : 'trailPrice'
  const initial = {
    qty: asInput(order.qty),
    limitPrice: asInput(order.limit_price),
    stopPrice: asInput(order.stop_price),
    trail: asInput(order.trail_percent ?? order.trail_price),
    timeInForce: order.time_in_force as TimeInForce,
  }
  const [form, setForm] = useState(initial)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')

  const changes: ReplaceOrderPayload = {}
  if (order.qty && form.qty !== initial.qty) changes.qty = Number(form.qty)
  if (usesLimit(order.type) && form.limitPrice !== initial.limitPrice) {
    changes.limitPrice = Number(form.limitPrice)
  }
  if (usesStop(order.type) && form.stopPrice !== initial.stopPrice) {
    changes.stopPrice = Number(form.stopPrice)
  }
  if (order.type === 'trailing_stop' && form.trail !== initial.trail) {
    changes[trailField] = Number(form.trail)
  }
  if (form.timeInForce !== initial.timeInForce) changes.timeInForce = form.timeInForce
  const invalid = Object.values(changes).some((v) => typeof v === 'number' && !(v > 0))

  const submit = async (event: React.FormEvent) => {
    event.preventDefault()
    setBusy(true)
    setError('')
    const res = await tradingServices.replaceOrder(order.alpaca_order_id, changes)
    setBusy(false)
    if (res?.status === 200) onDone(`${order.symbol} order updated.`)
    else setError(errorMessage(res, 'Could not modify this order.'))
  }

  const field = (name: 'qty' | 'limitPrice' | 'stopPrice' | 'trail', label: string) => (
    <label className='trading-page__field'>
      <span>{label}</span>
      <input
        type='number'
        min='0'
        step='any'
        value={form[name]}
        onChange={(e) => setForm((f) => ({ ...f, [name]: e.target.value }))}
      />
    </label>
  )

  return (
    <form
      className='trading-page__modify'
      onSubmit={submit}
      aria-label={`Modify ${order.symbol} order`}
    >
      <div className='trading-page__row'>
        {order.qty ? field('qty', 'Shares') : null}
        {usesStop(order.type) ? field('stopPrice', 'Stop price') : null}
        {usesLimit(order.type) ? field('limitPrice', 'Limit price') : null}
        {order.type === 'trailing_stop'
          ? field('trail', order.trail_percent ? 'Trail (%)' : 'Trail (USD)')
          : null}
        <label className='trading-page__field'>
          <span>Time in force</span>
          <select
            value={form.timeInForce}
            onChange={(e) => setForm((f) => ({ ...f, timeInForce: e.target.value as TimeInForce }))}
          >
            {tifChoices(order).map((t) => (
              <option key={t} value={t}>
                {TIME_IN_FORCE_LABEL[t]}
              </option>
            ))}
          </select>
        </label>
      </div>
      {error ? (
        <p className='trading-page__error' role='alert'>
          {error}
        </p>
      ) : null}
      <div className='trading-page__actions'>
        <button
          type='submit'
          className='trading-page__btn'
          disabled={busy || invalid || Object.keys(changes).length === 0}
        >
          {busy ? 'Saving…' : 'Save changes'}
        </button>
        <button type='button' className='trading-page__btn is-secondary' onClick={onCancel}>
          Keep as is
        </button>
      </div>
    </form>
  )
}

const ORDER_COLUMNS = 7

/** Order history with cancel and modify for open orders. */
const OrdersTable = ({
  orders,
  onChanged,
}: {
  orders: TradingOrder[]
  onChanged: () => Promise<void> | void
}) => {
  const [editing, setEditing] = useState<string | null>(null)
  const [notice, setNotice] = useState('')

  const cancel = async (orderId: string) => {
    setNotice('')
    await tradingServices.cancelOrder(orderId)
    await onChanged()
  }

  return (
    <>
      {notice ? (
        <p className='trading-page__ok' role='status'>
          {notice}
        </p>
      ) : null}
      <table className='trading-page__table'>
        <thead>
          <tr>
            <th>Symbol</th>
            <th>Side</th>
            <th>Type</th>
            <th>Size</th>
            <th>Status</th>
            <th>Filled</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {orders.map((o) => {
            const open = isOpenOrder(o)
            return (
              <React.Fragment key={o.alpaca_order_id}>
                <tr>
                  <td>
                    <Link to={`/dashboard/trading/${o.symbol}`}>{o.symbol}</Link>
                  </td>
                  <td>{o.side}</td>
                  <td>
                    {describeOrderType(o)}
                    {o.time_in_force !== 'day' ? (
                      <div className='trading-page__muted'>
                        {TIME_IN_FORCE_LABEL[o.time_in_force as TimeInForce] ?? o.time_in_force}
                      </div>
                    ) : null}
                  </td>
                  <td>{o.qty ? `${Number(o.qty)} sh` : usd(o.notional)}</td>
                  <td>{o.status}</td>
                  <td>
                    {o.filled_qty && Number(o.filled_qty) > 0
                      ? `${Number(o.filled_qty)} @ ${usd(o.filled_avg_price)}`
                      : '—'}
                  </td>
                  <td className='trading-page__cell-actions'>
                    {open && isModifiable(o) ? (
                      <button
                        type='button'
                        className='trading-page__link'
                        onClick={() =>
                          setEditing(editing === o.alpaca_order_id ? null : o.alpaca_order_id)
                        }
                      >
                        Modify
                      </button>
                    ) : null}
                    {open ? (
                      <button
                        type='button'
                        className='trading-page__link'
                        onClick={() => void cancel(o.alpaca_order_id)}
                      >
                        Cancel
                      </button>
                    ) : null}
                  </td>
                </tr>
                {editing === o.alpaca_order_id ? (
                  <tr>
                    <td colSpan={ORDER_COLUMNS}>
                      <ModifyOrderForm
                        order={o}
                        onCancel={() => setEditing(null)}
                        onDone={async (text) => {
                          setEditing(null)
                          setNotice(text)
                          await onChanged()
                        }}
                      />
                    </td>
                  </tr>
                ) : null}
              </React.Fragment>
            )
          })}
        </tbody>
      </table>
    </>
  )
}

export { OrdersTable }
